import React, {useEffect, useState} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { createStyles, makeStyles } from '@material-ui/core';
import { AlertMessage, CategoryItemNode, IdAndName, Template, Desk, User, Channel, Publisher } from '../../../shared/types';
import {
	AlertBox, ExtendedTheme, Loader, Option, TextBlock, Button, InlineText, Divider, BreadcrumbsText,
	Breadcrumbs, Link, Heading, ExpansionPanel, ExpansionPanelSummary, ExpansionPanelDetails, Select, TextField
} from 'neon-design-system';
import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import { useUser } from '../../providers/UserProvider';
import { prepareStaticDataList, categoriesToList } from '../../../utils/list-utils';
import { formatPublishDate } from '../../../utils/date.utils';
import { getContributorRoleUsers, getUsersWithNames } from '../../../utils/filtering.utils';
import ManageTemplateDialog from '../../ui/ManageTemplateDialog';
import { translateErrorMessage } from '../../../shared/error-handling';
import { Formik, Form, useField, FieldAttributes } from 'formik';
import * as Yup from 'yup';
import { categoryFieldsFragment, categoriesAsTreePartial } from '../../../gql/categories';
import gql from 'graphql-tag';
import { FormSnackBarType, generateSnackBarFormMessage } from '../../../utils/form.utils';
import { ManageTemplateInstance } from '../../ui/ManageTemplate';
import { useNotification } from '../../providers/NotificationProvider';
import TemplateEditor from '../../ui/admin/TemplateEditor';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { USER_PUBLISH_CHANNELS } from '../../../gql/users';
import { topicsOrCategories } from '../../../utils/utils';

const returnListUrl = baseUrls.publisher.templates.list;

const useStyles = makeStyles((theme: ExtendedTheme) => createStyles({
	container: {
		display: 'flex',
		width: '100%',
		height: '100%',
		paddingLeft: theme.spacing(2)
	},
	editContainer: {
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
		paddingBottom: theme.spacing(6),
		marginTop: theme.spacing(2)
	},
	dialogCaption: {
		display: 'flex'
	},
	dialogCheckbox: {
		marginTop: theme.spacing(2)
	},
	rightSideBarContainer: {
		backgroundColor: theme.palette.grey[800],
		marginLeft: theme.spacing(2),
		minWidth: 368,
		width: 368,
		display: 'flex',
		flexDirection: 'column'
	},
	sidebarHeader: {
		backgroundColor: theme.themeColors.grey[700]
	},
	sidebarDetails: {
		backgroundColor: theme.palette.grey[800],
		display: 'block',
		'&>div:not(:last-child)': {
			marginBottom: theme.spacing(2)
		}
	},
	sidebarTemplateDetails: {
		backgroundColor: theme.palette.grey[800],
		display: 'block',
		'&>div:not(:last-child)': {
			marginBottom: theme.spacing(1)
		}
	},
	expansionPanel: {
		overflow: 'visible'
	},
	editContent: {
		maxWidth: 1200,
		width: '100%'
	},
	alert: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2)
	},
	editor: {
		marginTop: theme.spacing(2),
		maxWidth: '820px'
	},
	form: {
		height: '100%'
	},
	headerMainAction: {
		padding: theme.spacing(2),
		'&>button': {
			width: '100%'
		}
	},
	statusRow: {
		marginBottom: theme.spacing(1),
		'&:last-child': {
			marginBottom: 0
		}
	},
	divider: {
		width: '100%'
	},
	statusText: {
		marginLeft: theme.spacing(1),
		fontWeight: 'bold'
	},
	heading: {
		marginTop: theme.spacing(4),
		marginBottom: theme.spacing(1)
	},
	marginBottom1: {
		marginBottom: theme.spacing(1)
	},
	metaInfo: {
		margin: theme.spacing(2)
	},
	notificationText: {
		display: 'block',
		marginTop: theme.spacing(1)
	}
}));

const ADD_TEMPLATE = gql`
mutation addTemplate($item: TemplateInputAdd) {
	addTemplate(item: $item) { _id }
}`;

const EDIT_TEMPLATE = gql`
mutation editTemplate($item: TemplateInputEdit) {
	editTemplate(item: $item) { _id }
}`;

const DELETE_TEMPLATE = gql`
mutation DeleteTemplate($id: ID!) {
	deleteTemplate(id: $id) { _id }
}`;

const GET_TEMPLATE = gql`
query templatePage($id: ID, $withTemplate: Boolean!) {
	document: findTemplateById(id: $id) @include(if: $withTemplate) {
		_id
		author
		title
		html
		publishedBy { _id name type }
		contributors { _id name }
		categories { _id name }
		tags { _id name }
		created
		version
		lastEdited
		premiumChannel
	}
	tags { _id name }
	desks { _id name }
	users: contributors { _id scopes name }
	${categoriesAsTreePartial}
}
${categoryFieldsFragment}`;

const MAGIC_SPLIT = '-_-';

const toIds = (arr?: Option[]) => arr?.map(o => o.value) ?? [];
const toOption = (x?: Partial<IdAndName>): Option | undefined => x && ({ value: x._id || '', label: x.name || '' });
const toOptions = (arr?: Partial<IdAndName>[]): Option[] => arr?.map(toOption) as Option[] || [];
const toPublisherOption = (publishedBy: Publisher): Option => ({value: [publishedBy.type, publishedBy._id].join(MAGIC_SPLIT), label: publishedBy.name});
const publisherOptionToPublisherInput = (value: string) => {
	const [type, _id] = value.split(MAGIC_SPLIT);
	return {type, _id};
};
const channelToPublisher = (channel: Channel): Publisher => ({...channel.owner, type: channel.ownerType});

export const TemplateAddEdit = () => {
	const classes = useStyles();
	const { _id } = useParams();
	const { showMessage } = useNotification();
	const { userDetails } = useUser();
	const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
	const [pageMessage, setPageMessage] = useState<AlertMessage>({ style: 'info', message: null });

	const { data, loading } = useQuery<{ document?: Template; categoriesAsTree: CategoryItemNode[]; desks: Desk[]; users: User[]; tags: IdAndName[] }>(
		GET_TEMPLATE, { variables: { id: _id, withTemplate: !!_id }, fetchPolicy: 'network-only' }
	);

	const [getChannels, { loading: channelsLoading, data: channelsData }] = useLazyQuery(USER_PUBLISH_CHANNELS, {
		fetchPolicy: 'network-only'
	});

	useEffect(() => {
		getChannels();
	}, [getChannels])

	const [addDbAction] = useMutation(ADD_TEMPLATE);
	const [editDbAction] = useMutation(EDIT_TEMPLATE);
	const [deleteDbAction] = useMutation(DELETE_TEMPLATE);

	const history = useHistory();
	const goBack = () => history.push(returnListUrl);

	if (loading || !data || channelsLoading || !channelsData) return <Loader />;

	const handleDelete = (id?: string, title?: string) => () => {
		if (!id) return;
		try {
			deleteDbAction({ variables: { id } });
			if (title) {
				showMessage(
					generateSnackBarFormMessage(
						ManageTemplateInstance.Template,
						title,
						FormSnackBarType.Deleted
					)
				);
			}
			goBack();
		} catch (error) {
			setPageMessage({ style: 'error', message: translateErrorMessage(error.message) });
		}
	};

	const findUsedChannel = (channelId?: string): Channel | undefined => channelsData?.me?.channelsToPublish?.find((c: Channel) => c._id.toString() === channelId);

	const template = data.document;
	const editMode = !!template;
	const currentChannel = findUsedChannel(template?.premiumChannel?.toString());

	return (
		<Formik
			initialValues={{
				title: template?.title || '',
				html: template?.html || '',
				publishedBy: template?.publishedBy ? toPublisherOption(template?.publishedBy) : currentChannel ? toPublisherOption(channelToPublisher(currentChannel)) : null,
				contributors: toOptions(getUsersWithNames(template?.contributors as User[])) || [],
				categories: toOptions(template?.categories) || [],
				tags: toOptions(template?.tags) || [],
				premiumChannel: toOption(currentChannel)
			}}
			validationSchema={
				Yup.object({
					title: Yup.string().required('This field is required.'),
					premiumChannel: Yup.object().nullable().required('This field is required.')
				})
			}
			onSubmit={async (values, { setSubmitting }) => {
				try {
					setSubmitting(true);
					const saveFn = editMode ? editDbAction : addDbAction;
					await saveFn({
						variables: {
							item: {
								_id,
								publishedBy: values.publishedBy?.value ? publisherOptionToPublisherInput(`${values.publishedBy.value}`) : undefined,
								contributors: toIds(values.contributors),
								author: userDetails?.id,
								title: values.title || undefined,
								html: values.html || '',
								categories: toIds(values.categories),
								tags: toIds(values.tags),
								premiumChannel: values.premiumChannel?.value || null,
								version: data?.document?.version
							}
						}
					});
					showMessage(
						generateSnackBarFormMessage(
							ManageTemplateInstance.Template,
							values.title,
							editMode ? FormSnackBarType.Updated : FormSnackBarType.Added
						)
					);
					goBack();
				} catch (error) {
					setSubmitting(false);
					setPageMessage({ style: 'error', message: translateErrorMessage(error.message) });
				}
			}}
		>
			{({ values, setFieldValue, errors }) =>
				<Form translate="yes" className={classes.form}>
					<div className={classes.container}>
						<div className={classes.editContainer}>
							<div className={classes.editContent}>
								<Breadcrumbs>
									<Link to={returnListUrl}>Templates</Link>
									<BreadcrumbsText>{editMode ? 'Edit template' : 'New template'}</BreadcrumbsText>
								</Breadcrumbs>
								<Heading className={classes.heading} styleName="title1">
									{!template?.title ?
										'Article template' :
										`${editMode && 'Edit template'} ${template?.title}`
									}
								</Heading>
								<TextBlock styleName="body">Add some placeholder content to define the base structure of this article.</TextBlock>
								<div className={classes.alert}>
									<AlertBox
										id="article-add-edit-alert"
										visible={!!pageMessage.message}
										children={pageMessage.message}
										styleName={pageMessage.style}
										onClose={() => setPageMessage({ style: 'info', message: null })}
									/>
								</div>
								<div className={classes.editor}>
									{
										// Froala bug. Can't send multiple updates in quick succession.
										// See https://github.com/froala/react-froala-wysiwyg/issues/221#issuecomment-605609436
									}
									{(values.html !== undefined || !editMode) &&
										<TemplateEditor
											initialHtml={values.html ?? ' '}
											errorMessage={errors.html}
											onChange={(html) => setFieldValue('html', html)}
										/>
									}
								</div>
							</div>
						</div>
						<div className={classes.rightSideBarContainer}>
							<div className={classes.sidebarHeader}>
								<div className={classes.headerMainAction}>
									<Button id="save-template-button" colorType="primary" size="large" type="submit">
										{!editMode ? 'Save template' : 'Update template'}
									</Button>
								</div>
								{editMode && <>
									<Divider className={classes.divider} />
									<div className={classes.metaInfo}>
										<div className={classes.statusRow}>
											<InlineText styleName="footnote">Last edit:</InlineText>
											<InlineText styleName="footnote" className={classes.statusText}>
												{template && formatPublishDate(new Date(template.lastEdited), true)}
											</InlineText>
										</div>
										<div className={classes.statusRow}>
											<InlineText styleName="footnote">Created:</InlineText>
											<InlineText styleName="footnote" className={classes.statusText}>
												{template && formatPublishDate(new Date(template.created), true)}
											</InlineText>
										</div>
									</div>
									<Divider className={classes.divider} />
									<div className={classes.metaInfo}>
										<Button id="delete-template-button" className={classes.marginBottom1} colorType="error" size="small" onClick={() => setDeleteDialogOpen(true)}>
											Delete this template
										</Button>
										<TextBlock styleName="caption1">Deleting a template won't affect existing articles.</TextBlock>
									</div>
								</>}
								<Divider className={classes.divider} />
							</div>
							<div>
								<SidebarPanel label="Template details" templateDetails>
									<TextInput id="template-name-input" label="Template name" name="title" placeholder="Daily Report {{Today}}" />
									<TextBlock styleName="caption1" className={classes.marginBottom1}>The template name is used to generate the <strong>article title</strong> every time new content is created.</TextBlock>
									<TextBlock styleName="caption1">Use the <strong>{'{{Today}}'}</strong> placeholder to have the publish date generated automatically for you.</TextBlock>
								</SidebarPanel>
								<SidebarPanel label="Article details">
									<SelectInput
										id="premium-channel-select"
										label="Channel"
										name="premiumChannel"
										options={prepareStaticDataList(channelsData?.me?.channelsToPublish)}
										onChange={(value) => {
											if (!value) {
												setFieldValue('publishedBy', null);
												return;
											}
											const channel = findUsedChannel(`${value.value}`);
											setFieldValue('publishedBy', channel ? toPublisherOption(channelToPublisher(channel)) : null)}
										}
									/>
									<SelectInput
										id="contributor-select"
										multi
										label="Contributors"
										name="contributors"
										options={prepareStaticDataList(getContributorRoleUsers(getUsersWithNames(data?.users)))}
										required={false}
									/>
								</SidebarPanel>
								<SidebarPanel label="Taxonomies">
									<CategoryInput treeCategories={data?.categoriesAsTree} required={false} />
									<SelectInput id="tag-select" multi label="Tags" name="tags" options={prepareStaticDataList(data?.tags)} required={false} />
								</SidebarPanel>
							</div>
							<ManageTemplateDialog
								onDelete={handleDelete(template?._id, template?.title)}
								submitBtnColorType="error"
								dialogMessage={<>
									You are about to delete the template <strong> {values.title}</strong>.
									This action cannot be undone, so you must be sure and confirm.
									<span className={classes.notificationText} >
										Please rest assured that all the articles created from this template won't be affected nor deleted.
									</span>
								</>}
								title={'Delete this template?'}
								submitBtnText={'Yes, delete'}
								dialogOpenState={isDeleteDialogOpen}
								closeDialog={() => setDeleteDialogOpen(false)} />
						</div>
					</div>
				</Form>
			}
		</Formik>
	);
};

const SidebarPanel: React.FC<{ label: string; templateDetails?: boolean }> = ({ label, children, templateDetails = false }) => {
	const classes = useStyles();
	return (
		<ExpansionPanel className={classes.expansionPanel} defaultExpanded={true}>
			<ExpansionPanelSummary>{label}</ExpansionPanelSummary>
			<ExpansionPanelDetails
				className={!templateDetails ? classes.sidebarDetails : classes.sidebarTemplateDetails}
			>
				{children}
			</ExpansionPanelDetails>
		</ExpansionPanel>
	);
};

const TextInput: React.FC<FieldAttributes<{ label: string; name: string; placeholder: string; id: string }>> = (
	{ label, name, placeholder, id }
) => {
	const [{ value, ...field }, meta] = useField({ name });
	return <TextField
		{...field}
		value={value}
		label={label}
		error={meta.touched && !!meta.error}
		errorText={meta.error}
		placeholder={placeholder}
		id={id}
	/>;
};

interface SelectInputProps {
	label: string;
	name: string;
	options: Option[];
	multi?: boolean;
	hideSelectedOptions?: boolean;
	id?: string;
	required?: boolean;
	onChange?: (value: Option) => void;
	disabled?: boolean;
}

const SelectInput: React.FC<SelectInputProps> = (props) => {
	const [{ value, ...field }, meta, helpers] = useField({ multiple: true, name: props.name });
	return <Select
		{...field}
		value={value}
		label={props.label}
		error={meta.touched && !!meta.error}
		errorText={meta.error}
		options={props.options}
		onChange={(option) => {
			helpers.setValue(option);
			props.onChange?.(option);
		}}
		onBlur={() => helpers.setTouched(true)}
		multi={props.multi}
		disabled={props.disabled}
		menuPlacement="auto"
		hideSelectedOptions={props.hideSelectedOptions ?? true}
		placeholder="Select from the list..."
		id={props.id}
		required={props.required}
	/>;
};

const CategoryInput: React.FC<any> = ({ treeCategories = [], required }) => {
	const [{ value }] = useField({ name: 'categories' });
	const categoryOptions = categoriesToList<Option>(treeCategories, (cat) => ({
		label: cat.name,
		value: cat._id,
		disabled: !!value?.find((v: any) => v.value === cat._id),
		indentation: cat.level
	}))
	return (
		<SelectInput
			id="category-select"
			multi
			hideSelectedOptions={false}
			label={topicsOrCategories(true, true)}
			name="categories"
			options={categoryOptions}
			required={required}
		/>
	);
};

export default TemplateAddEdit;
